import React from 'react'
import { animated, useSpring } from 'react-spring'
import { CloseIcon, HamburgerIcon } from '@mote/assets/icons'
import { useCallbackRef } from '@mote/helpers'
import { preventBubblingEvent } from '@mote/helpers/browser'
import { KeyboardKey, __DEV__ } from '@mote/helpers/constants'
import styled, { css, th } from '@xstyled/styled-components'

const DEFAULT_BUTTON_SIZE = 40
const DEFAULT_ICON_SIZE = 24
const ICON_SIZE_RATIO = DEFAULT_BUTTON_SIZE / DEFAULT_ICON_SIZE

const animationConfig = {
  mass: 1,
  frictionLight: 20,
  frictionHeavy: 30,
  tension: 575,
  delay: 125
}

const Bars = ({ styles, transitions }) => {
  return (
    <BarsContainer className='group' aria-hidden>
      <Bar style={{ transform: transitions.transformTop, ...styles.topBar }} />
      <Bar style={{ opacity: transitions.fadeMiddle, ...styles.middleBar }} />
      <Bar
        style={{ transform: transitions.transformBottom, ...styles.bottomBar }}
      />
    </BarsContainer>
  )
}

export const HamburgerButton = React.forwardRef(
  (
    {
      onClick: _onClick,
      isAnimated = true,
      size = DEFAULT_BUTTON_SIZE,
      isOpen = false,
      ...props
    },
    ref
  ) => {
    const onClick = useCallbackRef(_onClick)
    const Icon = isOpen ? CloseIcon : HamburgerIcon
    const { styles, transitions, iconSize } = React.useMemo(() => {
      const iconSize = size / ICON_SIZE_RATIO
      const barHeight = (size - 4) / iconSize
      const middleBarHeight = barHeight / 2
      const middle = iconSize / 2 - middleBarHeight
      const position = middle / 2

      const styles = {
        topBar: { top: `${position}px` },
        middleBar: { top: `${middle}px` },
        bottomBar: { bottom: `${position}px` }
      }

      const init = {
        transformTop: `translateY(0px) rotate(0deg)`,
        fadeMiddle: 1,
        transformBottom: `translateY(0px) rotate(0deg)`
      }

      const leave = [
        {
          transformTop: `translateY(${middle - position}px) rotate(0deg)`,
          fadeMiddle: 0,
          transformBottom: `translateY(-${middle - position}px) rotate(0deg)`,
          config: { clamp: true }
        },
        {
          ...init,
          config: {
            clamp: false,
            friction: animationConfig.frictionLight,
            tension: animationConfig.tension
          },
          delay: animationConfig.delay
        }
      ]

      const enter = [
        {
          transformTop: `translateY(${middle - position}px) rotate(0deg)`,
          fadeMiddle: 0,
          transformBottom: `translateY(-${middle - position}px) rotate(0deg)`,
          config: { clamp: true }
        },
        {
          transformTop: `translateY(${middle - position}px) rotate(45deg)`,
          fadeMiddle: 0,
          transformBottom: `translateY(-${middle - position}px) rotate(-45deg)`,
          config: {
            clamp: false,
            friction: animationConfig.frictionLight,
            tension: animationConfig.tension
          },
          delay: animationConfig.delay
        }
      ]

      return { styles, transitions: { init, enter, leave }, iconSize }
    }, [size])

    const [transitionsStyles, api] = useSpring(() => ({
      ...transitions.init,
      config: {
        mass: animationConfig.mass,
        friction: animationConfig.frictionHeavy,
        tension: animationConfig.tension
      }
    }))

    const handleClick = React.useCallback(
      event => {
        onClick?.(event)

        if (!isAnimated) return

        api.start({
          to: !isOpen ? transitions.leave : transitions.enter
        })
      },
      [isOpen, isAnimated, onClick, transitions]
    )

    return (
      <Root
        ref={ref}
        size={size}
        type='button'
        aria-label='Toggle menu'
        isAnimated={isAnimated}
        onClick={event => {
          preventBubblingEvent(event)
          handleClick(event)
        }}
        onKeyDown={event => {
          switch (event.keyCode) {
            case KeyboardKey.Space:
            case KeyboardKey.Enter:
              preventBubblingEvent(event)
              handleClick(event)
              break
          }
        }}
        {...props}
      >
        {!isAnimated && <Icon w={`${iconSize}px`} h={`${iconSize}px`} />}
        {isAnimated && <Bars styles={styles} transitions={transitionsStyles} />}
      </Root>
    )
  }
)

const Root = styled.button`
  --hamburger-button-size: ${({ size }) => size}px;

  ${({ isAnimated, size }) =>
    isAnimated &&
    css`
      --hamburger-icon-size: ${size / ICON_SIZE_RATIO}px;
      --hamburger-bar-height: ${(size - 4) / (size / ICON_SIZE_RATIO)}px;
      --hamburger-bar-width: calc(var(--hamburger-icon-size) / 1.6551724138);
    `}

  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: black;
  border-radius: 4px;
  border: 0;
  padding: 0;
  appearance: none;
  user-select: none;
  background-color: transparent;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  transition: all 150ms ease-in;
  box-sizing: border-box;
  width: var(--hamburger-button-size);
  height: var(--hamburger-button-size);
  -webkit-tap-highlight-color: transparent;

  &:hover,
  &:focus {
    background: ${th.color('grey.l4')};
  }

  &:focus {
    box-shadow: outline-primary;
  }

  span.group {
    height: var(--hamburger-icon-size);
    width: var(--hamburger-icon-size);
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.4;
    `};
`

const Bar = styled(animated.span)`
  position: absolute;
  left: calc((var(--hamburger-icon-size) - var(--hamburger-bar-width)) / 2);
  width: var(--hamburger-bar-width);
  height: var(--hamburger-bar-height);
  background: currentColor;
  border-radius: var(--hamburger-bar-height);
  flex-shrink: 0;
`

const BarsContainer = styled.span`
  display: block;
  position: relative;
  flex-shrink: 0;
  pointer-events: none;
`

if (__DEV__) {
  HamburgerButton.displayName = 'HamburgerButton'
}
